'use client'

import {
  Border1,
  ColorBaseTransparent,
  ColorBaseWhite,
  ColorDecorativePurple90,
  ColorTextDark,
  SizeBorder1,
  SizeRadiusRounded,
  SizeRadiusS,
  SizeSpacing2,
  SizeSpacing3,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { Loader } from '@snowball-tech/fractal/dist/src/components/Loader'
import {
  Paper,
  PaperElevations,
} from '@snowball-tech/fractal/dist/src/components/Paper'
import { Progress } from '@snowball-tech/fractal/dist/src/components/Progress'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cn } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { useSearchParams } from 'next/navigation'

import { useCallback, useState } from 'react'
import { useAsync } from 'react-async-hook'

import constant from 'lodash/fp/constant'
import isEmpty from 'lodash/fp/isEmpty'
import noop from 'lodash/fp/noop'

import { Apps } from '../../enums'
import { getSigninUrl, getUrl } from '../../helpers/navigation'
import { isUserConnected } from '../../helpers/user'
import useApi from '../../hooks/useApi'
import useApiWindow from '../../hooks/useApiWindow'
import useAppName from '../../hooks/useAppName'
import useTranslations from '../../hooks/useTranslations'
import useUser from '../../hooks/useUser'
import { asPx, spacings } from '../../styles/helpers'
import LegacyTable from '../LegacyTable'
import SubscribeButton from '../SubscribeButton'

type Props = {
  pollId: string
  className?: string
  editionUuid?: string
  forHtml?: boolean
  id?: string
  interactive?: boolean
  options?: Array<{ option: string; uuid?: string }>
  showResults?: boolean
  title?: string
}
export default function Poll({
  className,
  editionUuid,
  forHtml = false,
  id,
  interactive = true,
  options,
  pollId,
  showResults = false,
  title,
}: Props) {
  const $t = useTranslations()
  const searchParams = useSearchParams()

  const classicEndpoints = useApi()
  const windowEndpoints = useApiWindow(true)
  const endpoints = forHtml ? windowEndpoints : classicEndpoints

  const { isLoading: isUserLoading, user } = useUser()
  const isConnected = isUserConnected(user, searchParams)

  const appName = useAppName()

  const isStatic = !isEmpty(editionUuid) && !isEmpty(title) && !isEmpty(options)
  const isInteractive = !isStatic && interactive

  const [optionUuidToVoteFor, setOptionUuidToVoteFor] = useState('')
  const [isFocusedOnSubscribe, setIsFocusedOnSubscribe] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [currentVote, setCurrentVote] = useState<string | null>(null)
  const [totalVotes, setTotalVotes] = useState(0)
  const asyncPoll = useAsync(async () => {
    if (isEmpty(pollId) || isUserLoading) {
      return null
    }

    try {
      const poll = await endpoints.polls.get(pollId)
      setCurrentVote(poll?.vote?.optionUuid || null)

      const totalVotesCount = poll?.options.reduce(
        (accumulator, { votes }) => accumulator + votes,
        0,
      )
      setTotalVotes(totalVotesCount)

      setIsLoading(constant(false))

      return poll
    } catch {
      return null
    }
  }, [user?.uuid, pollId])

  const actualResult = isStatic
    ? {
        error: null,
        execute: noop,
        loading: false,
        result: {
          createdAt: new Date().toISOString(),
          editionUuid: '',
          options: (options ?? []).map((option) => ({ ...option, votes: 0 })),
          question: title,
          status: 'open',
          uuid: pollId,
        },
      }
    : asyncPoll

  const getPercentage = useCallback(
    (votes: number) => {
      if (totalVotes === 0) {
        return 0
      }

      return Math.round((votes / totalVotes) * 100)
    },
    [totalVotes],
  )

  const handleVote = async (optionUuid: string | undefined) => {
    if (isEmpty(pollId) || !isInteractive || isEmpty(optionUuid)) {
      return
    }

    if (!isConnected) {
      setOptionUuidToVoteFor(optionUuid)

      return
    }

    await endpoints.polls.vote.cast(pollId, optionUuid)

    asyncPoll.execute()
    setCurrentVote(optionUuid)
  }

  if (
    !actualResult.loading &&
    !isUserLoading &&
    (actualResult.error || isEmpty(actualResult.result))
  ) {
    return false
  }

  const onSignedIn = () => {
    setOptionUuidToVoteFor('')
    setIsFocusedOnSubscribe(constant(true))

    setIsLoading(true)

    setTimeout(() => {
      asyncPoll.execute()
    }, 1000)
  }

  const titleContent = (
    <>
      <Typography
        className={forHtml ? undefined : 'w-full'}
        inlineStyle={forHtml}
        style={forHtml ? { width: '100%' } : undefined}
        variant="heading-4"
      >
        📊️
      </Typography>

      <Typography
        className={forHtml ? undefined : 'w-full'}
        inlineStyle={forHtml}
        style={forHtml ? { width: '100%' } : undefined}
        variant="heading-4"
      >
        {$t.notEmpty(actualResult.result?.question || '') ||
          actualResult.result?.question}
      </Typography>
    </>
  )

  const paddingVertical = spacings[1] - spacings.quarter
  const minHeight =
    spacings[6] - paddingVertical * 2 - (asPx(SizeBorder1) as number) * 2

  const optionsContent = actualResult.result?.options.map((option, index) => {
    const button = (
      <Button
        fullStyle={forHtml}
        fullWidth
        href={
          isInteractive && !forHtml
            ? undefined
            : getUrl(
                Apps.Media,
                `vote/${pollId}/${option.uuid}?auth_token={{ insert auth_token }}`,
              )
        }
        inlineStyle={forHtml}
        style={
          forHtml
            ? {
                backgroundColor: ColorBaseTransparent,
                border: 'none',
                boxShadow: 'none',
                color: ColorTextDark,
                display: 'block',
                margin: '0 auto',
                maxHeight: 'fit-content',
                maxWidth: '100%',
                minHeight: asPx(minHeight, { withUnit: true }),
                padding: 0,
                pointerEvents: 'auto',
              }
            : undefined
        }
        target={isInteractive && !forHtml ? undefined : '_blank'}
        underlined={false}
        variant="display"
        wrapperStyles={
          forHtml
            ? {
                marginTop: 0,
                maxHeight: 'fit-content',
              }
            : undefined
        }
        onClick={
          isInteractive && !forHtml ? () => handleVote(option.uuid) : undefined
        }
      >
        <Typography
          className="max-w-full whitespace-break-spaces"
          inlineStyle={forHtml}
          style={
            forHtml
              ? {
                  lineHeight: asPx(minHeight, { withUnit: true }),
                  maxWidth: '100%',
                  minHeight: asPx(minHeight, { withUnit: true }),
                  whiteSpace: 'break-spaces',
                }
              : undefined
          }
          variant="body-1-median"
        >
          {$t.notEmpty(option.option) || option.option}
        </Typography>
      </Button>
    )

    return forHtml ? (
      <LegacyTable
        key={
          isEmpty(option.uuid)
            ? `poll-${actualResult.result?.uuid}-option-${index}`
            : `option-${option.uuid}`
        }
        center
        defaultCellStyle={{
          backgroundColor: ColorBaseWhite,
          border: Border1,
          borderRadius: SizeRadiusRounded,
          borderWidth: asPx(SizeBorder1, { withUnit: true }),
          margin: '0 auto',
          maxWidth: '100%',
          padding: `${asPx(paddingVertical, { withUnit: true })} ${asPx(
            spacings[3] - spacings.quarter,
            { withUnit: true },
          )}`,
        }}
      >
        {button}
      </LegacyTable>
    ) : (
      button
    )
  })

  return forHtml ? (
    <LegacyTable
      id={id}
      center
      col
      data-type="poll"
      defaultCellStyle={{ textAlign: 'center' }}
      elevation={PaperElevations.Higher}
      gap={spacings[3]}
      style={{
        backgroundColor: ColorDecorativePurple90,
        border: 0,
        borderRadius: SizeRadiusS,
        marginBottom: asPx(SizeSpacing3, { withUnit: true }),
        padding: `${asPx(SizeSpacing3, { withUnit: true })} ${asPx(
          SizeSpacing2,
          { withUnit: true },
        )}`,
      }}
    >
      <LegacyTable
        center
        col
        defaultCellStyle={{ textAlign: 'center' }}
        gap={spacings[1]}
        style={{
          borderRadius: SizeRadiusS,
        }}
      >
        {titleContent}
      </LegacyTable>

      <LegacyTable
        center
        className="preserve-legacy-poll-block"
        col
        defaultCellStyle={{ textAlign: 'center' }}
        gap={spacings[3]}
        importantWidth={false}
        style={{
          borderRadius: SizeRadiusS,
          maxWidth: 'min(100%, 320px)',
          padding: `${asPx(SizeSpacing2, { withUnit: true })} 0`,
          width: '100%',
        }}
      >
        {optionsContent}
      </LegacyTable>
    </LegacyTable>
  ) : (
    <div id={id} className="flex justify-center">
      <Paper
        className={cn(
          'flex w-full flex-col items-center gap-3 bg-decorative-purple-70 p-3',
          className,
        )}
        elevation={PaperElevations.Higher}
      >
        {(actualResult.loading || isLoading || isUserLoading) && <Loader />}

        {!actualResult.loading &&
          !isLoading &&
          !isEmpty(actualResult.result) && (
            <>
              <div className="flex w-full flex-col items-center gap-1 text-center">
                {titleContent}
              </div>

              {!isConnected && !isEmpty(optionUuidToVoteFor) && (
                <div className="flex flex-col items-center gap-3 rounded-sm px-3">
                  <Typography
                    className="text-center"
                    element="div"
                    variant="body-1-median"
                  >
                    {$t.rich('poll.signin_signup.details')}
                  </Typography>

                  <SubscribeButton
                    redirectOnSuccess={false}
                    returnUrl={getUrl(
                      Apps.Media,
                      `vote/${pollId}/${optionUuidToVoteFor}`,
                    )}
                    withForm
                    onStartSubscribe={() => setIsFocusedOnSubscribe(true)}
                    onSubmitted={onSignedIn}
                  />

                  {!isFocusedOnSubscribe && (
                    <div>
                      <Typography element="span" variant="caption-median">
                        {$t('general.or')}
                      </Typography>
                      &nbsp;
                      <Typography
                        className="inline-block"
                        href={getSigninUrl(
                          Apps.Media,
                          getUrl(
                            Apps.Media,
                            `vote/${pollId}/${optionUuidToVoteFor}`,
                          ),
                        )}
                        target={
                          appName === Apps.Creators ? '_blank' : undefined
                        }
                        variant="caption-link"
                      >
                        {$t('subscribe.signin.label').toLocaleLowerCase()}
                      </Typography>
                    </div>
                  )}
                </div>
              )}

              {(isConnected || isEmpty(optionUuidToVoteFor)) &&
                (isEmpty(currentVote) || !isInteractive) &&
                !showResults && (
                  <div className="flex w-full max-w-[320px] flex-col items-center gap-3 py-2">
                    {optionsContent}
                  </div>
                )}

              {isConnected &&
                ((!isEmpty(currentVote) && isInteractive) || showResults) && (
                  <div className="flex w-full flex-col gap-3">
                    {asyncPoll.result?.options.map((result, index) => {
                      const percentage = getPercentage(result.votes)
                      const variant =
                        !isEmpty(currentVote) && currentVote === result.uuid
                          ? 'caption-bold'
                          : 'caption-median'

                      return (
                        <div
                          key={`poll-${asyncPoll.result?.uuid}-result-${result.uuid || index}`}
                          className="flex w-full flex-col gap-half"
                        >
                          <div className="flex w-full justify-between gap-2">
                            <Typography variant={variant}>
                              {$t.notEmpty(result.option) || result.option}
                            </Typography>

                            <Typography variant={variant}>
                              {percentage}&nbsp;%
                            </Typography>
                          </div>

                          <Progress max={100} value={percentage} />
                        </div>
                      )
                    })}

                    <Typography
                      className="w-full !py-1 text-center"
                      variant="caption-median"
                    >
                      {$t('edition.poll.total_votes', { totalVotes })}
                      {asyncPoll.result?.status === 'closed'
                        ? ` · ${$t('edition.poll.closed')}`
                        : ''}
                    </Typography>
                  </div>
                )}
            </>
          )}
      </Paper>
    </div>
  )
}
